import { BaseHTMLElement, customElement, onEvent } from 'dom-native';

import { getImageUrl } from '@/services/image-url';
import { escapeHtml, renderMarkdown } from '@/services/markdown';
import type { ChatImage, ChatMessage } from '@/types/chat';
import { icon } from '@/utils/icons';

function escapeAttribute(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

@customElement('help-agent-message-item')
export class HelpAgentMessageItem extends BaseHTMLElement {
  private _message: ChatMessage | null = null;

  init(): void {
    super.init();
    this.render();
  }

  set messageData(message: ChatMessage) {
    this._message = message;
    if (this.initialized) {
      this.render();
    }
  }

  get messageData(): ChatMessage | null {
    return this._message;
  }

  private render(): void {
    const message = this._message;

    if (!message) {
      this.innerHTML = '';
      return;
    }

    const contentHtml = this.renderContent(message);
    const attachmentsHtml = this.renderAttachments(message);
    const imagesHtml = this.renderImages(message.images);

    this.className = `help-agent-message-item is-${message.role} is-${message.state}${message.attachments?.length ? ' has-attachments' : ''}`;

    if (message.role === 'user') {
      this.innerHTML = `
        <article
          class="help-agent-message-card help-agent-message-card-user"
          data-test="help-widget-user-message"
        >
          <div class="help-agent-message-user-bubble" data-test="help-widget-user-bubble">
            <div class="help-agent-message-avatar help-agent-message-avatar-user">
              ${icon('user-circle', { size: 'var(--ha-message-user-icon-size)' })}
            </div>
            <div class="help-agent-message-user-content" data-test="help-widget-user-content">
              ${contentHtml}
              ${attachmentsHtml}
            </div>
          </div>
        </article>
      `;
      return;
    }

    this.innerHTML = `
        <article
          class="help-agent-message-card help-agent-message-card-assistant"
          data-test="help-widget-assistant-message"
        >
          <div class="help-agent-message-assistant-side" data-test="help-widget-assistant-side">
            <div class="help-agent-message-avatar help-agent-message-avatar-assistant">
              ${icon('assistant-stars', {
                width: 'var(--ha-message-assistant-icon-width)',
                height: 'var(--ha-message-assistant-icon-height)',
              })}
            </div>
          </div>
        <div class="help-agent-message-main" data-test="help-widget-assistant-main">
          <div class="help-agent-message-body" data-test="help-widget-assistant-body">
            ${contentHtml}
            ${attachmentsHtml}
            ${imagesHtml}
          </div>
        </div>
      </article>
    `;
  }

  private renderContent(message: ChatMessage): string {
    if (message.state === 'loading') {
      return `
        <div class="help-agent-typing-indicator" aria-label="Assistant is typing">
          <span></span>
          <span></span>
          <span></span>
        </div>
      `;
    }

    if (message.role === 'assistant') {
      return `<div class="help-agent-markdown">${renderMarkdown(message.content)}</div>`;
    }

    if (!message.content.trim()) {
      return '';
    }

    return `<div class="help-agent-plain-text">${escapeHtml(message.content).replace(/\n/g, '<br>')}</div>`;
  }

  private renderAttachments(message: ChatMessage): string {
    if (!message.attachments || message.attachments.length === 0) {
      return '';
    }

    const imageAttachments = message.attachments.filter(
      (attachment) => attachment.kind === 'image' && Boolean(attachment.previewUrl),
    );

    return `
      <div
        class="help-agent-message-attachments help-agent-message-attachments-${message.role}"
        data-test="help-widget-message-attachments"
        role="list"
        aria-label="Message attachments"
      >
        ${message.attachments.map((attachment) => `
          <article
            class="help-agent-message-attachment is-${attachment.kind}"
            data-test="help-widget-message-attachment"
            data-test-key="${escapeAttribute(attachment.kind)}"
            role="listitem"
          >
            ${attachment.kind === 'image' && attachment.previewUrl
              ? `
                <button
                  type="button"
                  class="help-agent-message-attachment-preview"
                  data-test="help-widget-message-attachment-preview"
                  data-attachment-preview-index="${imageAttachments.findIndex((item) => item.id === attachment.id)}"
                  aria-label="Preview image ${escapeAttribute(attachment.name)}"
                >
                  <span class="help-agent-message-attachment-thumb" aria-hidden="true">
                    <img src="${escapeAttribute(attachment.previewUrl)}" alt="" loading="lazy" />
                  </span>
                  <span class="help-agent-message-attachment-label" title="${escapeAttribute(attachment.name)}">
                    ${escapeHtml(attachment.name)}
                  </span>
                </button>
              `
              : `
                <div
                  class="help-agent-message-attachment-content"
                  data-test="help-widget-message-attachment-file"
                  aria-label="Attached file ${escapeAttribute(attachment.name)}"
                >
                  <span class="help-agent-message-attachment-thumb" aria-hidden="true">
                    ${icon('attachment', { height: 'var(--ha-message-attachment-file-icon-height)' })}
                  </span>
                  <span class="help-agent-message-attachment-label" title="${escapeAttribute(attachment.name)}">
                    ${escapeHtml(attachment.name)}
                  </span>
                </div>
              `}
          </article>
        `).join('')}
      </div>
    `;
  }

  private renderImages(images?: ChatImage[]): string {
    if (!images || images.length === 0) {
      return '';
    }

    const apiBaseUrl = this._getAiHelperApiBaseUrl();

    return `
      <div class="help-agent-message-images" data-test="help-widget-message-images">
        ${images.map((image, index) => `
          <div class="help-agent-message-image-card" data-test="help-widget-message-image-card">
            <button
              type="button"
              class="help-agent-message-image-button"
              data-test="help-widget-message-image-button"
              data-image-index="${index}"
              aria-label="Open image ${index + 1}"
            >
              <img
                class="help-agent-message-image"
                src="${escapeHtml(getImageUrl(image.url, apiBaseUrl))}"
                alt="${escapeHtml(image.caption || 'Reference image')}"
                loading="lazy"
              />
            </button>
            ${image.caption ? `<div class="help-agent-message-image-caption">${escapeHtml(
              image.step_number ? `Step ${image.step_number}: ${image.caption}` : image.caption,
            )}</div>` : ''}
          </div>
        `).join('')}
      </div>
    `;
  }

  private _getAiHelperApiBaseUrl(): string | null {
    const widget = this.closest('help-agent-app') as { config?: { apiUrl?: string | null } } | null;
    return widget?.config?.apiUrl ?? null;
  }

  @onEvent('click', '.help-agent-message-image-button')
  onImageClick(event: Event & { selectTarget: HTMLElement }): void {
    if (!this._message?.images?.length) {
      return;
    }

    const target = event.selectTarget as HTMLButtonElement;
    const startIndex = Number.parseInt(target.dataset.imageIndex || '0', 10);

    this.dispatchEvent(new CustomEvent('help-agent:open-gallery', {
      bubbles: true,
      composed: true,
      detail: {
        images: this._message.images,
        startIndex,
      },
    }));
  }

  @onEvent('click', '[data-attachment-preview-index]')
  onAttachmentPreviewClick(event: Event & { selectTarget: HTMLElement }): void {
    if (!this._message?.attachments?.length) {
      return;
    }

    const imageAttachments = this._message.attachments.filter(
      (attachment) => attachment.kind === 'image' && Boolean(attachment.previewUrl),
    );
    const startIndex = Number.parseInt(
      event.selectTarget.getAttribute('data-attachment-preview-index') || '-1',
      10,
    );

    if (startIndex < 0 || startIndex >= imageAttachments.length) {
      return;
    }

    const images: ChatImage[] = imageAttachments.map((attachment) => ({
      url: attachment.previewUrl || '',
      caption: attachment.name,
    }));

    this.dispatchEvent(new CustomEvent('help-agent:open-gallery', {
      bubbles: true,
      composed: true,
      detail: {
        images,
        startIndex,
      },
    }));
  }
}
